import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { RoleDeleteDialogComponent } from '../role-delete-dialog/role-delete-dialog.component';
import { RoleUserDTO } from '../../../core/models/role.model';
import { RoleService } from '../../../core/services/role.service';
import { ClientService } from '../../../core/services/client.service';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatOptionModule } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RoleFormDialogComponent } from '../role-form-dialog/role-form-dialog.component';
import { RoleDetailDialogComponent } from '../role-detail-dialog/role-detail-dialog.component';
import { RolePermissionService } from '../../../core/services/role-permission.service';

interface ExtendedRoleDTO extends RoleUserDTO {
  clientName?: string;
  type: 'realm' | 'client';
}

interface ClientWithRoles {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  roles: ExtendedRoleDTO[];
  expanded?: boolean;
}

@Component({
  selector: 'app-role-list',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressBarModule,
    MatChipsModule,
    MatMenuModule,
    MatDividerModule,
    MatPaginatorModule,
    MatTableModule,
    MatButtonModule,
    MatTabsModule,
    MatExpansionModule,
    MatTooltipModule
  ],
  templateUrl: './role-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./role-list.component.scss']
})
export class RoleListComponent implements OnInit, OnDestroy {
  realmRoles: ExtendedRoleDTO[] = [];
  clientsWithRoles: ClientWithRoles[] = [];
  filteredRealmRoles: ExtendedRoleDTO[] = [];
  filteredClientsWithRoles: ClientWithRoles[] = [];

  totalRealmRoles = 0;
  realmPageSize = 20;
  realmCurrentPage = 0;

  loading = false;
  searchControl = new FormControl('');
  selectedTabIndex = 0;

  displayedColumns: string[] = ['avatar', 'roleInfo', 'composite', 'actions'];

  private destroy$ = new Subject<void>();

  openDropdownRole: RoleUserDTO | null = null;

  constructor(
    private roleService: RoleService,
    private clientService: ClientService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private rolePermissionService: RolePermissionService
  ) { }

  ngOnInit(): void {
    this.loadAllData();
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());
  }

  private loadAllData(): void {
    this.loading = true;
    const types = this.rolePermissionService.hasRole('SCA_SUPER_ADMIN')
      ? ['external-app', 'internal-app']
      : ['external-app'];

    forkJoin({
      realmRoles: this.roleService.getAllRealmRoles(0, 1000, '', true),
      clients: this.clientService.getClientsByTypesWithRoles(types)
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.realmRoles = results.realmRoles.content.map(role => ({
            ...role,
            type: 'realm' as const
          }));
          this.loadClientsWithRoles(results.clients);
        },
        error: (error) => {
          console.error('Erreur de chargement:', error);
          this.showError('Erreur lors du chargement des données');
          this.loading = false;
        }
      });
  }

  private loadClientsWithRoles(clients: any[]): void {
    const clientRolesRequests = clients.map(client =>
      this.roleService.getClientRolesByClientId(client.id, 0, 1000)
        .pipe(catchError(() => of({ content: [] })))
    );
    forkJoin(clientRolesRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rolesResponses) => {
          this.clientsWithRoles = clients.map((client, index) => ({
            id: client.id,
            clientId: client.clientId,
            name: client.name || client.clientId,
            description: client.description,
            roles: rolesResponses[index].content.map(role => ({
              ...role,
              type: 'client' as const,
              clientName: client.name || client.clientId
            })),
            expanded: false
          }));
          this.applyFilters();
          this.loading = false;
        },
        error: (error) => {
          console.error('Erreur chargement rôles clients:', error);
          this.showError('Erreur lors du chargement des rôles clients');
          this.loading = false;
        }
      });
  }

  private applyFilters(): void {
    const searchTerm = this.searchControl.value?.toLowerCase().trim();
    if (this.selectedTabIndex === 0) {
      this.filterClientRoles(searchTerm);
    } else {
      this.filterRealmRoles(searchTerm);
    }
  }

  private filterRealmRoles(searchTerm?: string): void {
    let filtered = [...this.realmRoles];
    if (searchTerm) {
      filtered = filtered.filter(role =>
        role.name.toLowerCase().includes(searchTerm) ||
        role.description?.toLowerCase().includes(searchTerm)
      );
    }
    this.totalRealmRoles = filtered.length;
    const start = this.realmCurrentPage * this.realmPageSize;
    this.filteredRealmRoles = filtered.slice(start, start + this.realmPageSize);
  }

  private filterClientRoles(searchTerm?: string): void {
    if (!searchTerm) {
      this.filteredClientsWithRoles = [...this.clientsWithRoles];
      return;
    }
    this.filteredClientsWithRoles = this.clientsWithRoles
      .map(client => ({
        ...client,
        roles: client.roles.filter(role =>
          role.name.toLowerCase().includes(searchTerm) ||
          role.description?.toLowerCase().includes(searchTerm) ||
          client.name.toLowerCase().includes(searchTerm)
        )
      }))
      .filter(client => client.roles.length > 0 || client.name.toLowerCase().includes(searchTerm));
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.realmCurrentPage = 0;
    this.searchControl.setValue('');
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  onRealmPageChange(event: PageEvent): void {
    this.realmCurrentPage = event.pageIndex;
    this.realmPageSize = event.pageSize;
    this.filterRealmRoles(this.searchControl.value?.toLowerCase().trim());
  }

  // Ouverture des modales (popup)

  addRole(): void {
    this.dialog.open(RoleFormDialogComponent, {
      width: '700px',
      disableClose: true,
      data: { mode: 'create' }
    }).afterClosed().subscribe(result => {
      if (result) this.loadAllData();
    });
  }

  editRole(role: ExtendedRoleDTO): void {
    // console.log("TEST : ", role);
    
    this.dialog.open(RoleFormDialogComponent, {
      width: '700px',
      disableClose: true,
      data: {
        mode: 'edit',
        role,
        clientId: role.type === 'client' ? role.containerId : undefined,
        isRealm: role.type === 'realm'
      }
    }).afterClosed().subscribe(result => {
      if (result) this.loadAllData();
    });
  }

  viewRole(role: ExtendedRoleDTO): void {
    this.dialog.open(RoleDetailDialogComponent, {
      width: '800px',
      disableClose: true,
      data: {
        role,
        clientName: role.clientName,
        isRealm: role.type === 'realm'
      }
    }).afterClosed().subscribe(result => {
      if (result === 'refresh') this.loadAllData();
    });
  }

  openDeleteDialog(role: ExtendedRoleDTO): void {
    const dialogRef = this.dialog.open(RoleDeleteDialogComponent, {
      width: '450px',
      data: { role, clientName: role.clientName }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.deleteRole(role);
    });
  }

  private deleteRole(role: ExtendedRoleDTO): void {
    this.loading = true;
    const deleteObservable = role.type === 'realm'
      ? this.roleService.deleteRealmRole(role.name)
      : this.roleService.deleteClientRole(role.containerId!, role.name);
    deleteObservable.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess(`Rôle "${role.name}" supprimé avec succès`);
          this.loadAllData();
        },
        error: (error) => {
          console.error('Erreur suppression:', error);
          this.showError('Erreur lors de la suppression du rôle');
          this.loading = false;
        }
      });
  }

  // ---- Utilitaires ----

  getInitials(name: string): string {
    if (!name) return '?';
    const words = name.split(/[\s-_]+/);
    return words.length >= 2 ? (words[0][0] + words[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
  }

  getAvatarColor(name: string): string {
    const colors = ['#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e', '#16a085', '#27ae60', '#2980b9', '#8e44ad', '#2c3e50', '#f39c12', '#e67e22', '#e74c3c', '#95a5a6', '#d35400'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  /**
 * Retourne le nombre total de rôles clients (pour l'onglet Clients)
 */
  get totalRoles(): number {
    return this.clientsWithRoles.reduce(
      (total, client) => total + (client.roles?.length || 0),
      0
    );
  }

  getCompositeRolesCount(): number {
    const realmComposite = this.realmRoles.filter(r => r.composite).length;
    const clientComposite = this.clientsWithRoles.reduce((s, c) => s + c.roles.filter(r => r.composite).length, 0);
    return realmComposite + clientComposite;
  }

  getClientRolesCount(client: ClientWithRoles): number {
    return client.roles.length;
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fermer', { duration: 3000, panelClass: ['success-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }
  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }

  toggleDropdown(event: Event, role: ExtendedRoleDTO): void {
    event.stopPropagation();
    this.openDropdownRole = this.openDropdownRole === role ? null : role;
  }

  isDropdownOpen(role: ExtendedRoleDTO): boolean {
    return this.openDropdownRole === role;
  }

  // Gestion de l'accordéon client
  toggleClient(client: ClientWithRoles): void {
    client.expanded = !client.expanded;
  }

  // Getter pour le total des rôles
  getTotalRoles(): number {
    return this.realmRoles.length + this.clientsWithRoles.reduce((acc, c) => acc + c.roles.length, 0);
  }
}